import { useEffect, useState } from 'react';

import { ColDef, IHeaderParams } from 'ag-grid-community';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useSearchParams } from 'react-router-dom';

import { useGetStringMapsQuery } from '@/app/services/lookups';
import { StringMap } from '@/rtk-query.gql';
import {
  useEstimationHeaderValQuery,
  useFreshShipmentCacheInvalidation,
  useUpdateShipmentGroupTransaction,
} from '@/services/fresh-shipment/hooks';
import { StringMapBase } from '@/types';

import { ShipmentEstimations } from '../utils/constants';

interface ShipmentEstimationsDropdownProps extends IHeaderParams {
  shipmentEstimations: StringMapBase[];
  options: Array<{ label: string; value: string }>;
  setColumnDef: (columnDef: ColDef) => void;
}

export const ShipmentEstimationsDropdown = (
  props: ShipmentEstimationsDropdownProps,
) => {
  const invalidateCache = useFreshShipmentCacheInvalidation();
  const [params, setParams] = useSearchParams();
  const groupId = params.get('group-id');
  const [selectedField, setSelectedField] = useState<string | number>(
    props.column.getColId(),
  );
  const { data: shipmentEstimationsData } = useGetStringMapsQuery({
    objectTypeCode: ShipmentEstimations.OBJECT_TYPE_CODE as number,
    attributeName: ShipmentEstimations.NAME as string,
  });

  const { data: estimationHeaderVal } = useEstimationHeaderValQuery({
    shipGroupId: groupId,
  });

  const estimationHeaderValList = estimationHeaderVal;

  const { mutateAsync: updateShipmentGroupTransaction } =
    useUpdateShipmentGroupTransaction();

  useEffect(() => {
    const selectedOption = shipmentEstimationsData?.find(
      (option: StringMap) =>
        option.attributeValue === parseInt(estimationHeaderValList, 10),
    )?.attributeValue as number;
    if (estimationHeaderValList && shipmentEstimationsData) {
      setSelectedField(selectedOption);
    }
  }, [estimationHeaderValList, shipmentEstimationsData]);

  const onFieldChange = (e: DropdownChangeEvent) => {
    setSelectedField(e.value);
    const columnDefs = props.api.getColumnDefs();
    const currentColId = props.column.getColId();
    const updatedColumnDefs = columnDefs?.map((colDef) => {
      if ('field' in colDef && colDef.field === currentColId) {
        return { ...colDef, field: String(e.value) };
      }
      return colDef;
    });
    props.api.setGridOption('columnDefs', updatedColumnDefs);

    const value = shipmentEstimationsData?.find(
      (option: StringMap) => option.attributeValue === e.value,
    )?.attributeValue;

    updateShipmentGroupTransaction({
      id: groupId as string,
      field: 'estimations',
      value: String(value),
    }).then(() => {
      invalidateCache([
        'EstimationHeaderVal',
        'FertilizationListFreshShipment',
      ]);
    });
  };

  return (
    <Dropdown
      value={selectedField}
      options={shipmentEstimationsData}
      onChange={onFieldChange}
      optionLabel="value"
      optionValue="attributeValue"
      placeholder="Select a field"
      className="shipment-estimations-dropdown"
      tooltip={
        shipmentEstimationsData?.find(
          (option: StringMap) => option.attributeValue === selectedField,
        )?.value as string
      }
    />
  );
};
